import { easingLinear } from 'pf-frontend-common/src/helper/easing/linear';
import { objectMerge } from 'pf-frontend-common/src/helper/object/merge';
import { BrowserViewport } from 'pf-frontend-common/src/module/browser/viewport';
import { BrowserViewportAwareInterface } from 'pf-frontend-common/src/module/browser/viewport-aware.interface';
import { DomElementEvent } from 'pf-frontend-common/src/module/dom/element.event';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { StatsContextLocalInterface } from 'pf-frontend-common/src/module/stats/context/local.interface';
import { StatsEntityEnum } from 'pf-frontend-common/src/module/stats/entity.enum';
import { StatsAttributionServiceInterface } from 'pf-frontend-common/src/service/stats-attribution/service.interface';
import { StatsDataServiceInterface } from 'pf-frontend-common/src/service/stats-data/service.interface';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';

import { BrokerModel } from 'common/data/broker/model';
import { BrokerStatsDataAdapter } from 'common/data/broker/stats/data-adapter';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { BrokerSerpStatsPaginationAdapter } from 'common/module/broker-serp/stats/pagination-adapter';
import { BrokerSerpStore } from 'common/module/broker-serp/store';
import { DataStore } from 'common/module/data/store';
import { DataStoreEvent } from 'common/module/data/store.event';
import { DataStoreDispatchEventInterface } from 'common/module/data/store/dispatch-event.interface';
import { DataStoreOptionsInterface } from 'common/module/data/store/options.interface';
import { BrokerStoreServiceInterface } from 'common/service/broker-store/service.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';
import { BrokerCardTemplatePropsInterface } from 'desktop/module/broker/card/template-props.interface';
import { BrokerListTemplatePropsInterface } from 'desktop/module/broker/list/template-props.interface';

/**
 * @deprecated
 *
 * TODO-FE[WEB-1602]: Remove this in favor of common BrokerListViewStore
 */
export class BrokerListViewStore extends DataStore implements BrowserViewportAwareInterface {
  /**
   * @inheritDoc
   */
  protected state: BrokerListTemplatePropsInterface;

  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    protected windowService: WindowServiceInterface,
    protected browserViewport: BrowserViewport,
    protected statsService: StatsServiceInterface,
    protected statsDataService: StatsDataServiceInterface,
    protected statsAttributionService: StatsAttributionServiceInterface,
    protected brokerStoreService: BrokerStoreServiceInterface,
    protected brokerSerpStore: BrokerSerpStore,
    protected brokerStatsDataAdapter: BrokerStatsDataAdapter
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public getBrowserViewport(): BrowserViewport {
    return this.browserViewport;
  }

  /**
   * @inheritDoc
   */
  public initialize(options?: DataStoreOptionsInterface): void {
    this.state = {
      disabled: false,
      title: i18nTranslate('Broker directory'),
      brokers: [],
    };
    this.refreshState();

    // Attach observers
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public getState(): BrokerListTemplatePropsInterface {
    return this.state;
  }

  /**
   * Update brokers
   */
  public updateBrokers(): void {
    this.refreshState();

    this.setState(this.state, this.updateBrokers);
  }

  /**
   * @inheritDoc
   */
  public updateViewport(): void {
    // View disabled
    if (this.state.disabled === true) {
      // Don't update the viewport
      return;
    }

    // For each broker
    const iMax = this.state.brokers.length;
    for (let i = 0; i < iMax; i++) {
      // Reset watcher (which will check the element's position within the viewport)
      this.browserViewport.resetWatcher(String(i));
    }
  }

  /**
   * Updates in the viewport the HTML element linked to the broker's index within the list
   */
  public updateHtmlElement(index: number, element: HTMLElement): void {
    this.browserViewport.registerWatcher(String(index), element);
  }

  /**
   * @inheritDoc
   */
  public updateDisabled(disabled: boolean): void {
    // Update state
    this.state.disabled = disabled;
    this.setState(this.state, this.updateDisabled);

    // Update viewport
    this.updateViewport();
  }

  /**
   * Attach observers
   */
  protected observers(): void {
    // Agent SERP store
    this.brokerSerpStore.getEventEmitter().addListener(DataStoreEvent.updateState, this.onUpdateStateBrokerSerpStore);

    // Browser viewport
    this.browserViewport.getEventEmitter().addListener(DomElementEvent.enterViewport, this.onEnterViewport);
  }

  /**
   * Get stats context local to this view
   */
  protected getStatsContext(): StatsContextLocalInterface {
    return {
      pagination: new BrokerSerpStatsPaginationAdapter(this.brokerSerpStore.getState()).getData(),
    };
  }

  /**
   * Refreshes state
   *
   * @deprecated Stinky "refresh state" keywords detected, most likely a logic somewhere else is wrong
   */
  protected refreshState(): void {
    const brokerSerpStoreState = this.brokerSerpStore.getState();
    const brokers: BrokerCardTemplatePropsInterface[] = [];

    // For each result
    brokerSerpStoreState.results.list.forEach((brokerId, index) => {
      // Retrieve loaded broker model
      const brokerModel = this.brokerStoreService.getOneById(brokerId);

      // No broker loaded
      if (!brokerModel) {
        return;
      }

      // Default position without pagination
      let position = index + 1;

      // Pagination params
      if (brokerSerpStoreState.params['page[number]'] && brokerSerpStoreState.params['page[limit]']) {
        // Add previous records count
        position += (brokerSerpStoreState.params['page[number]'] - 1) * brokerSerpStoreState.params['page[limit]'];
      }

      // Register stats entity
      this.statsDataService.getBrokerStore().add(this.brokerStatsDataAdapter.getData(brokerModel, position, index + 1));

      // Add broker card
      brokers.push({
        broker: brokerModel,
        onCreateTemplate: this.onCreateTemplate,
        onClickBroker: this.onClickBroker,
      });

      // Log stats
      this.statsService.brokerLoad(parseInt(brokerId, 10), this.getStatsContext());
    });

    // Set state
    this.state = {
      ...this.state,
      brokers,
    };
  }

  /**
   * Triggers when broker SERP store state updates
   */
  protected onUpdateStateBrokerSerpStore = (storeDispatchEvent: DataStoreDispatchEventInterface): void => {
    this.windowService.scrollToY(0, 500, easingLinear);

    if (storeDispatchEvent.action === this.brokerSerpStore.updateResults) {
      this.updateBrokers();
    } else {
      // @deprecated This is equivalent to the stinky "refresh state"
      this.setState(this.state);
    }
  };

  /**
   * Clicked broker
   */
  protected onClickBroker = (e: Event, brokerModel: BrokerModel): void => {
    // Log stats
    this.statsService.brokerClick(
      parseInt(brokerModel.id, 10),
      objectMerge({}, this.getStatsContext(), {
        authentication: {
          attributionId: this.statsAttributionService.push(StatsEntityEnum.broker, brokerModel.id),
        },
      })
    );
  };

  /**
   * Created template
   */
  protected onCreateTemplate = (element: HTMLElement, index: number): void => {
    this.updateHtmlElement(index, element);
  };

  /**
   * An element entered the browser viewport
   */
  protected onEnterViewport = (watcherId: string): void => {
    // View is disabled
    if (this.state.disabled === true) {
      return;
    }

    // Broker card doesn't exist
    if (!this.state.brokers[parseInt(watcherId, 10)]) {
      return;
    }

    // Log stats
    this.statsService.brokerImpression(
      parseInt(this.state.brokers[parseInt(watcherId, 10)].broker.id, 10),
      this.getStatsContext()
    );
  };
}
